import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { RefreshCw, Download, AlertTriangle, Check, X } from 'lucide-react'
import {
  runGpsAudit, getGpsAuditStatus, getGpsAuditResults, resolveGpsAudit, GpsAuditResult,
} from '../services/api'
import { useRole } from '../lib/RoleContext'
import { hasRole } from '../lib/role'

const M_TO_FT = 3.28084

function haversineFt(aLat?: number | null, aLng?: number | null, bLat?: number | null, bLng?: number | null): number | null {
  if (aLat == null || aLng == null || bLat == null || bLng == null) return null
  const R = 6371000
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(bLat - aLat), dLng = toRad(bLng - aLng)
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2
  return Math.round(R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s)) * M_TO_FT)
}

const GAP_REASONS = new Set(['no_customer', 'customer_not_found', 'no_customer_pin', 'unknown_band'])
const STATUS_ORDER = ['flagged', 'eligible', 'ok', 'auto_applied', 'resolved', 'dismissed', 'skipped']

const csvCell = (v: unknown): string => {
  const s = v == null ? '' : String(v)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

export default function AddressAudit() {
  const qc = useQueryClient()
  const role = useRole()
  const canManage = hasRole(role, 'admin')
  const [statusFilter, setStatusFilter] = useState('')
  const [bandFilter, setBandFilter] = useState('')
  const [msg, setMsg] = useState<string | null>(null)

  const status = useQuery({ queryKey: ['gpsAuditStatus'], queryFn: async () => (await getGpsAuditStatus()).data,
    refetchInterval: (q) => (q.state.data?.running ? 2000 : false) })
  const results = useQuery({ queryKey: ['gpsAuditResults'], queryFn: async () => (await getGpsAuditResults('')).data })

  const run = useMutation({
    mutationFn: () => runGpsAudit(true),
    onSuccess: () => { setMsg('Dry-run audit started…'); qc.invalidateQueries({ queryKey: ['gpsAuditStatus'] }) },
    onError: (e: any) => setMsg(e?.response?.data?.detail ?? 'Run failed'),
  })
  const resolve = useMutation({
    mutationFn: (v: { serial: string; action: 'apply' | 'dismiss' }) => resolveGpsAudit(v.serial, v.action),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gpsAuditResults'] }),
    onError: (e: any) => setMsg(e?.response?.data?.detail ?? 'Action failed'),
  })

  // When a run finishes (running flips false), refresh the results table.
  useEffect(() => {
    if (status.data?.running === false) qc.invalidateQueries({ queryKey: ['gpsAuditResults'] })
  }, [status.data?.running, qc])

  const rows = results.data ?? []
  const gaps = useMemo(() => rows.filter((r) => (r.reasons ?? []).some((x) => GAP_REASONS.has(x))), [rows])
  const audited = useMemo(() => rows.filter((r) => !gaps.includes(r)), [rows, gaps])

  const filtered = useMemo(() => audited.filter((r) =>
    (!statusFilter || r.status === statusFilter) &&
    (!bandFilter || String(r.band) === bandFilter)
  ).sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status)), [audited, statusFilter, bandFilter])

  const tally = useMemo(() => {
    const t: Record<string, number> = {}
    for (const r of audited) t[r.status] = (t[r.status] ?? 0) + 1
    return t
  }, [audited])

  const exportCsv = () => {
    const cols = ['serial_number', 'customer_id', 'customer_name', 'band', 'direction', 'status', 'reasons',
      'splynx_lat', 'splynx_lng', 'radio_lat', 'radio_lng', 'geocode_lat', 'geocode_lng',
      'pin_to_radio_ft', 'pin_to_geocode_ft', 'radio_to_geocode_ft']
    const line = (r: GpsAuditResult) => [
      csvCell(r.serial_number), csvCell(r.customer_id ?? ''), csvCell(r.customer_name ?? ''), csvCell(r.band ?? ''), csvCell(r.direction ?? ''),
      csvCell(r.status), csvCell((r.reasons ?? []).join('|')),
      csvCell(r.splynx_lat ?? ''), csvCell(r.splynx_lng ?? ''), csvCell(r.radio_lat ?? ''), csvCell(r.radio_lng ?? ''), csvCell(r.geocode_lat ?? ''), csvCell(r.geocode_lng ?? ''),
      csvCell(haversineFt(r.splynx_lat, r.splynx_lng, r.radio_lat, r.radio_lng) ?? ''),
      csvCell(haversineFt(r.splynx_lat, r.splynx_lng, r.geocode_lat, r.geocode_lng) ?? ''),
      csvCell(haversineFt(r.radio_lat, r.radio_lng, r.geocode_lat, r.geocode_lng) ?? ''),
    ].join(',')
    const csv = [cols.join(','), ...rows.map(line)].join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = url; a.download = `address-audit-${new Date().toISOString().slice(0, 10)}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  const running = status.data?.running
  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-xl font-bold text-gray-900">Address Audit</h1>
        <div className="flex gap-2">
          {canManage && (
            <button onClick={() => run.mutate()} disabled={running || run.isPending}
              className="flex items-center px-3 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 mr-1 ${running ? 'animate-spin' : ''}`} /> {running ? 'Running…' : 'Run full audit (dry run)'}
            </button>
          )}
          <button onClick={exportCsv} disabled={!rows.length}
            className="flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            <Download className="h-4 w-4 mr-1" /> CSV
          </button>
        </div>
      </div>
      <p className="text-sm text-gray-600 mb-3">Validates Splynx geolocation before any write. 6 GHz <span className="font-mono">to_splynx</span> is eligible only when the radio fix lands within 100 ft of the customer's geocoded address. Writes remain embargoed until this is reviewed.</p>
      {msg && <div className="text-sm text-gray-600 mb-3">{msg}</div>}
      {running && <div className="text-sm text-blue-600 mb-3">Audit in progress — {status.data?.processed ?? 0}/{status.data?.total ?? '?'} evaluated…</div>}

      <div className="flex flex-wrap gap-2 mb-4 text-xs">
        {STATUS_ORDER.filter((s) => tally[s]).map((s) => (
          <button key={s} onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
            className={`px-2 py-1 rounded border ${statusFilter === s ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-300'}`}>
            {s}: {tally[s]}
          </button>
        ))}
        <select value={bandFilter} onChange={(e) => setBandFilter(e.target.value)} className="px-2 py-1 border border-gray-300 rounded text-gray-600">
          <option value="">all bands</option><option value="5">5 GHz</option><option value="6">6 GHz</option>
        </select>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm divide-y divide-gray-200">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr>{['Serial', 'Customer', 'Band', 'Direction', 'Status', 'Reasons', 'pin↔radio', 'pin↔addr', 'radio↔addr', ''].map((h) => (
              <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>))}</tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((r) => (
              <tr key={r.serial_number} className={r.status === 'flagged' ? 'bg-amber-50' : r.status === 'eligible' ? 'bg-green-50' : ''}>
                <td className="px-3 py-2 font-mono text-xs">{r.serial_number}</td>
                <td className="px-3 py-2">{r.customer_name ?? r.customer_id ?? '—'}</td>
                <td className="px-3 py-2">{r.band ? `${r.band} GHz` : '—'}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.direction ?? '—'}</td>
                <td className="px-3 py-2">{r.status}</td>
                <td className="px-3 py-2 text-xs text-gray-600">{(r.reasons ?? []).join(', ')}</td>
                <td className="px-3 py-2 tabular-nums">{fmtFt(haversineFt(r.splynx_lat, r.splynx_lng, r.radio_lat, r.radio_lng))}</td>
                <td className="px-3 py-2 tabular-nums">{fmtFt(haversineFt(r.splynx_lat, r.splynx_lng, r.geocode_lat, r.geocode_lng))}</td>
                <td className={`px-3 py-2 tabular-nums ${(haversineFt(r.radio_lat, r.radio_lng, r.geocode_lat, r.geocode_lng) ?? 0) > 100 ? 'text-red-600 font-semibold' : ''}`}>
                  {fmtFt(haversineFt(r.radio_lat, r.radio_lng, r.geocode_lat, r.geocode_lng))}</td>
                <td className="px-3 py-2">
                  {r.status === 'eligible' || r.status === 'flagged' ? (
                    <div className="flex gap-1">
                      {canManage && <button title="Apply" disabled={resolve.isPending} onClick={() => resolve.mutate({ serial: r.serial_number, action: 'apply' })} className="p-1 text-green-700 hover:bg-green-100 rounded disabled:opacity-50"><Check className="h-4 w-4" /></button>}
                      {canManage && <button title="Dismiss" disabled={resolve.isPending} onClick={() => resolve.mutate({ serial: r.serial_number, action: 'dismiss' })} className="p-1 text-gray-500 hover:bg-gray-100 rounded disabled:opacity-50"><X className="h-4 w-4" /></button>}
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={10} className="px-3 py-6 text-center text-gray-400">No records. Run the audit.</td></tr>}
          </tbody>
        </table>
      </div>

      {gaps.length > 0 && (
        <div className="mt-6">
          <h2 className="text-sm font-semibold text-gray-800 flex items-center mb-2"><AlertTriangle className="h-4 w-4 mr-1 text-amber-500" /> Data gaps ({gaps.length})</h2>
          <p className="text-xs text-gray-500 mb-2">Customers that can't be auto-audited — fix the address/pin in Splynx or reconcile the customer match.</p>
          <div className="border border-gray-200 rounded-lg overflow-x-auto">
            <table className="min-w-full text-sm divide-y divide-gray-200">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase"><tr>{['Serial', 'Customer', 'Reasons'].map((h) => <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-100">
                {gaps.map((r) => (
                  <tr key={r.serial_number}>
                    <td className="px-3 py-2 font-mono text-xs">{r.serial_number}</td>
                    <td className="px-3 py-2">{r.customer_name ?? r.customer_id ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{(r.reasons ?? []).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function fmtFt(ft: number | null): string {
  return ft == null ? '—' : `${ft.toLocaleString()} ft`
}
